import { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { transferFormAction, transferFormSubmissions } from "../../store/forms/actionCreators";

import useShallowSelector from "../../hooks/useShalowSelector";
import { selectCurrentForm, selectIsTransfering, selectTransferData } from "../../store/forms/selectors";
import useFields from "../../hooks/useFields";
import { selectPages } from "../../store/pages/selectors";
import { selectFields } from "../../store/fields/selectors";
import { updateLogicRequest } from "../../store/logic/actionCreators";
import { debounce } from "../../helpers/debounce";

import styles from "./styles.module.scss";

interface IProps {
    transferInfo: {
        instance: string;
        compId: string;
    };
    setIsTransfer: (data: boolean) => void;
}

const TransferForm: FC<IProps> = ({ transferInfo, setIsTransfer }) => {
    const dispatch = useDispatch();
    const transferData = useShallowSelector(selectTransferData);
    const pages = useShallowSelector(selectPages);
    const fields = useShallowSelector(selectFields);
    const currentForm = useShallowSelector(selectCurrentForm);
    const isTransfering = useShallowSelector(selectIsTransfering);
    const { onAdd } = useFields();
    const [displayedText, setDisplayedText] = useState("");
    const [isCompleted, setIsCompleted] = useState(false);

    const messages = [
        "Hold tight, your form is shaping up beautifully! 🛠️",
        "Just a moment more, we’re aligning all your fields perfectly! ✨",
        "Thanks for your patience, we’re crafting the perfect layout! 🎨",
        "Your form is looking great! Adding those final touches now. 💪",
        "Good things take time, and this form will be worth it! 🌟",
        "We’re setting up all your settings and making it seamless for you! 🛡️",
        "You’re doing amazing, thank you for letting us handle this for you! ❤️",
        "Grab a coffee, we’re moving your fields and settings in no time! ☕",
        "Wow, you’re awesome! The layout is almost ready! ✍️",
        "Sit back and relax, your form is being tailored just for you. 😊",
        "Big changes are underway, and your form is almost done! 🌈",
    ];

    useEffect(() => {
        let index = 0;
        let charIndex = 0;
        let isTyping = true;

        const typeMessage = () => {
            if (isTyping) {
                setDisplayedText(prev => prev + messages[index][charIndex]);
                charIndex++;
                if (charIndex === messages[index].length) {
                    isTyping = false;
                    setTimeout(() => {
                        isTyping = true;
                        charIndex = 0;
                        index = (index + 1) % messages.length;
                        setDisplayedText("");
                    }, 1000);
                }
            }
        };

        const interval = setInterval(typeMessage, 100);
        return () => clearInterval(interval);
        //eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!transferData) return;
        if (transferData.fields) {
            if (transferData.pages) {
                Object.keys(transferData.pages).forEach(realPageIndex => {
                    const correctPageId = pages[Number(realPageIndex)];
                    const fieldsInPage = transferData.pages[realPageIndex];
                    fieldsInPage.forEach((fieldIndex: any) => {
                        const field = transferData.fields[fieldIndex];
                        onAdd(field.type, correctPageId._id, null, field);
                    });
                });
            }
        }
        //eslint-disable-next-line
    }, [transferData]);
    useEffect(() => {
        if (!transferData) return;
        if (fields.some(field => field._id.includes("temp"))) return;
        debounce(
            "update_logic_and_submissions_state",
            () => {
                const newLogic = transferData.logic.map(rule => {
                    const { actions, conditions } = rule;
                    return {
                        ...rule,
                        actions: actions.map(action => {
                            const actionFieldId = fields[Number(action._id)]?._id;
                            if (!actionFieldId) return {}; //if field  is not exists
                            return {
                                ...action,
                                _id: fields[Number(action._id)]._id,
                            };
                        }),
                        conditions: conditions.map(condition => {
                            const condFieldId = fields[Number(condition._id)]?._id;
                            if (!condFieldId) return {}; //if field  is not exists
                            return {
                                ...condition,
                                _id: fields[Number(condition._id)]._id,
                            };
                        }),
                    };
                });
                dispatch(updateLogicRequest({ rules: [...newLogic] }));
                const fieldsDictionary = transferData.wixFields.reduce<Record<number, string>>(
                    (acc, wixField, index) => {
                        const platformField = fields[index];
                        acc[wixField.id] = platformField._id;
                        return acc;
                    },
                    {},
                );
                dispatch(
                    transferFormSubmissions({
                        compId: transferInfo.compId!,
                        instance: transferInfo.instance,
                        formId: currentForm?._id!,
                        fieldsDictionary: fieldsDictionary,
                    }),
                );
            },
            1000,
        );
        //eslint-disable-next-line
    }, [fields]);

    useEffect(() => {
        if (transferInfo.instance && transferInfo.compId) {
            dispatch(transferFormAction({ compId: transferInfo.compId, instance: transferInfo.instance }));
        }
        //eslint-disable-next-line
    }, [transferInfo]);

    useEffect(() => {
        if (transferInfo.compId && !isTransfering) {
            setDisplayedText("Your form is ready! 🎉");
            setIsCompleted(true);
            const timeout = setTimeout(() => {
                setIsTransfer(false);
            }, 2000);

            return () => clearTimeout(timeout);
        }
        // eslint-disable-next-line
    }, [transferInfo, isTransfering]);

    return (
        <div className={styles.loaderContainer}>
            <div className={styles.loader}>
                <div className={styles.spinner}></div>
                <p className={styles.loaderText}>{isCompleted ? "Your form is ready! 🎉" : displayedText}</p>
            </div>
        </div>
    );
};
export default TransferForm;
